import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../../services/firebase';
import { trackLogin, saveUserToFirestore, resolveFinalEmail } from '../../utils';
import { AI_API_URL } from '../../constants';
import { Capacitor } from '@capacitor/core';

/**
 * 카카오 인증 관련 로직을 담당하는 커스텀 훅
 * @param {function} setLoginDialogOpen - 로그인 다이얼로그 상태 설정 함수
 * @param {function} showAlert - AlertDialog 표시 함수
 * @param {function} setAuthLoading - 로딩 상태 설정 함수
 * @returns {object} - 카카오 인증 관련 함수들
 */
export const useKakaoAuth = (setLoginDialogOpen, showAlert = null, setAuthLoading = null) => {

  /**
   * 네이티브 카카오 로그인 처리 함수 (iOS/Android)
   * @returns {Promise<boolean>} - 성공 여부
   */
  const handleNativeKakaoAuth = async () => {
    try {
      console.log('🔐 [Native] 카카오 로그인 시작...');
      
      // 올바른 플러그인 API 사용
      const { CapacitorKakao } = await import('capacitor-kakao-plugin');
      
      console.log('📱 카카오 플러그인 모듈 로드 완료');
      
      // 모달 닫기
      if (setLoginDialogOpen) {
        setLoginDialogOpen(false);
        console.log('🔒 카카오 네이티브 로그인 모달 닫기 완료');
      }
      
      // 네이티브 카카오 로그인 실행
      console.log('🚀 kakaoLogin() 호출 시작...');
      const result = await CapacitorKakao.kakaoLogin();
      console.log('✅ 네이티브 카카오 로그인 응답:', result);
      
      // 카카오 인증 완료 즉시 모달 닫고 로딩 시작
      if (setLoginDialogOpen) {
        setLoginDialogOpen(false);
        console.log('🔒 카카오 네이티브 로그인 모달 닫기 완료');
      }
      
      if (setAuthLoading) {
        setAuthLoading(true);
        console.log('🔄 카카오 로그인 로딩 시작');
      }
      
      // 액세스 토큰 추출
      const kakaoAccessToken = result.accessToken;
      if (!kakaoAccessToken) {
        throw new Error('카카오 액세스 토큰을 받지 못했습니다. 응답: ' + JSON.stringify(result));
      }
      
      console.log('🎫 카카오 액세스 토큰 수신 성공');
      
      // 백엔드 토큰 교환 처리
      const success = await processKakaoTokenExchange(kakaoAccessToken);
      
      // 모든 처리 완료 후 로딩 종료
      if (setAuthLoading) {
        setAuthLoading(false);
        console.log('🔄 카카오 로그인 로딩 종료 (성공)');
      }
      
      return success;
      
    } catch (error) {
      console.error('🚨 네이티브 카카오 로그인 실패:', error);
      console.error('🚨 에러 스택:', error.stack);
      
      // 🆕 카카오톡 미로그인 상태 감지 시 웹 fallback 시도
      if (error.message?.includes('KakaoTalk is installed but not connected to Kakao account') || 
          error.message?.includes('NotSupportError')) {
        
        console.log('🌐 카카오톡 미로그인 감지됨 - 웹 브라우저 fallback 시도...');
        
        try {
          // Browser 플러그인으로 웹 카카오 로그인 페이지 열기
          const { Browser } = await import('@capacitor/browser');
          const { AI_API_URL } = await import('../../constants');
          
          // 백엔드 카카오 OAuth URL로 리다이렉트
          const kakaoLoginUrl = `${AI_API_URL}/auth/kakao?platform=mobile_web`;
          
          console.log('🌐 웹 브라우저로 카카오 로그인 페이지 열기:', kakaoLoginUrl);
          
          await Browser.open({ url: kakaoLoginUrl });
          
          // 성공 반환 (실제 로그인은 브라우저에서 진행)
          return true;
          
        } catch (browserError) {
          console.error('🚨 웹 브라우저 fallback 실패:', browserError);
          
          // 웹 fallback도 실패하면 기본 에러 처리
          if (setAuthLoading) {
            setAuthLoading(false);
            console.log('🔄 카카오 로그인 로딩 종료 (웹 fallback 실패)');
          }
          
          const errorMessage = `카카오 로그인 실패 (웹 fallback 포함): ${browserError.message}`;
          if (showAlert) {
            showAlert('error', '카카오 로그인 실패', errorMessage);
          } else {
            alert(errorMessage);
          }
          return false;
        }
      }
      
      // 다른 에러들은 기존 처리 방식 유지
      if (setAuthLoading) {
        setAuthLoading(false);
        console.log('🔄 카카오 로그인 로딩 종료 (에러)');
      }
      
      if (error.message?.includes('USER_CANCELLED') || error.message?.includes('Cancel')) {
        console.log('👤 사용자가 카카오 로그인을 취소했습니다.');
        return false;
      }
      
      const errorMessage = `네이티브 카카오 로그인 실패: ${error.message}`;
      if (showAlert) {
        showAlert('error', '카카오 로그인 실패', errorMessage);
      } else {
        alert(errorMessage);
      }
      return false;
    }
  };

  /**
   * 웹 카카오 로그인 처리 함수
   * @returns {Promise<boolean>} - 성공 여부
   */
  const handleWebKakaoAuth = async () => {
    try {
      if (!window.Kakao) {
        console.error("카카오 SDK가 로드되지 않았습니다.");
        return false;
      }
      
      if (!window.Kakao.isInitialized()) {
        console.error("카카오 SDK가 초기화되지 않았습니다.");
        return false;
      }

      // 기존 토큰이 있다면 제거
      if (window.Kakao.Auth.getAccessToken()) {
        window.Kakao.Auth.logout();
      }

      return new Promise((resolve) => {
        window.Kakao.Auth.login({
          success: async function (authObj) {
            try {
              // 카카오 인증 완료 즉시 모달 닫고 로딩 시작
              if (setLoginDialogOpen) {
                setLoginDialogOpen(false);
                console.log('🔒 카카오 웹 로그인 모달 닫기 완료');
              }
              
              if (setAuthLoading) {
                setAuthLoading(true);
                console.log('🔄 카카오 로그인 로딩 시작');
              }
              
              const kakaoAccessToken = authObj.access_token;
              const success = await processKakaoTokenExchange(kakaoAccessToken);
              
              // 모든 처리 완료 후 로딩 종료
              if (setAuthLoading) {
                setAuthLoading(false);
                console.log('🔄 카카오 로그인 로딩 종료 (성공)');
              }
              
              resolve(success);
            } catch (e) {
              console.error("웹 카카오 로그인 처리 중 오류:", e);
              
              // 에러 시에도 로딩 종료
              if (setAuthLoading) {
                setAuthLoading(false);
                console.log('🔄 카카오 로그인 로딩 종료 (에러)');
              }
              
              if (showAlert) {
                showAlert('error', '카카오 로그인 실패', e.message);
              } else {
                alert("카카오 로그인 실패: " + e.message);
              }
              resolve(false);
            }
          },
          fail: function (err) {
            console.error("웹 카카오 로그인 실패:", err);
            
            // 실패 시에도 로딩 종료
            if (setAuthLoading) {
              setAuthLoading(false);
              console.log('🔄 카카오 로그인 로딩 종료 (실패)');
            }
            
            if (showAlert) {
              showAlert('error', '카카오 로그인 실패', JSON.stringify(err));
            } else {
              alert("카카오 로그인 실패: " + JSON.stringify(err));
            }
            resolve(false);
          },
          scope: 'profile_nickname, account_email'
        });
      });
    } catch (error) {
      console.error("웹 카카오 로그인 시도 중 오류:", error);
      if (showAlert) {
        showAlert('error', '카카오 로그인 오류', '카카오 로그인 시도 중 오류가 발생했습니다.');
      } else {
        alert("카카오 로그인 시도 중 오류가 발생했습니다.");
      }
      return false;
    }
  };

  /**
   * 카카오 토큰을 Firebase 커스텀 토큰으로 교환하는 공통 함수
   * @param {string} kakaoAccessToken - 카카오 액세스 토큰
   * @returns {Promise<boolean>} - 성공 여부
   */
  const processKakaoTokenExchange = async (kakaoAccessToken) => {
    try {
      const response = await fetch(`${AI_API_URL}/firebase_custom_token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kakaoAccessToken: kakaoAccessToken
        })
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Firebase 커스텀 토큰 발급 실패: ${errorData}`);
      }

      const { firebaseToken, email, userInfo, merged, uid } = await response.json();
      
      // 백엔드에서 계정 연동이 완료된 경우
      if (merged) {
        console.log('✅ [카카오연동] 백엔드에서 계정 연동 완료:', uid);
        
        // 기존 UID로 생성된 토큰으로 즉시 로그인
        const userCredential = await signInWithCustomToken(auth, firebaseToken);
        
        // AlertDialog 또는 기본 alert 사용
        if (showAlert) {
          showAlert('success', '연동 완료', '기존 이메일 계정에 카카오 로그인이 연동되었습니다! 이제 카카오로도 로그인할 수 있습니다.');
        } else {
          alert('기존 이메일 계정에 카카오 로그인이 연동되었습니다! 이제 카카오로도 로그인할 수 있습니다.');
        }
        
        // Google Analytics 계정 연동 이벤트 추적
        trackLogin('kakao_linked');
        
        return true;
      }
      
      // 새 계정 생성 또는 기존 카카오 계정 로그인 (기존 로직)
      const userCredential = await signInWithCustomToken(auth, firebaseToken);
      
      // 최종 이메일 결정
      const finalEmail = await resolveFinalEmail(email, userCredential.user.uid, 'kakao');
      
      // 카카오 로그인 정보와 함께 저장 (최종 이메일로 업데이트)
      await saveUserToFirestore(userCredential.user, {
        loginMethod: 'kakao',
        email: finalEmail,
        kakaoProfile: {
          ...userInfo,
          kakaoAccessToken: kakaoAccessToken
        }
      });
      
      // Google Analytics 로그인 이벤트 추적
      trackLogin('kakao');
      
      return true;
    } catch (error) {
      console.error('카카오 토큰 교환 실패:', error);
      throw error;
    }
  };

  /**
   * 메인 카카오 OAuth 인증 처리 함수 (플랫폼별 분기)
   * @returns {Promise<boolean>} - 성공 여부
   */
  const handleKakaoAuth = async () => {
    try {
      const platform = Capacitor.getPlatform();
      console.log(`🔐 [${platform}] 카카오 로그인 시작...`);
      
      if (platform === 'web') {
        console.log('🌐 웹 카카오 SDK 사용');
        return await handleWebKakaoAuth();
      } else {
        console.log('📱 네이티브 카카오 플러그인 사용');
        return await handleNativeKakaoAuth();
      }
      
    } catch (error) {
      console.error('🚨 카카오 로그인 실패:', error);
      
      const errorMessage = `카카오 로그인에 실패했습니다: ${error.message}`;
      if (showAlert) {
        showAlert('error', '카카오 로그인 실패', errorMessage);
      } else {
        alert(errorMessage);
      }
      return false;
    }
  };

  return {
    handleKakaoAuth
  };
}; 